// SectionCard.tsx — cross-section through the object. The slider sweeps the
// chosen world axis across the object's (client-side, outlier-trimmed)
// extent; each position is measured server-side (/measure kind "section")
// with a short debounce, and the returned convex hull (mm) is drawn to scale
// with its width, depth, area and perimeter.
import { useEffect, useMemo, useState } from 'react';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import { ui } from 'common';
import { api } from '../api';
import type { Scan, SectionResult } from '../api';
import { errorMessage, fmtMm } from '../format';
import { LABEL_OBJECT, cloudBounds } from '../splat/format';
import type { SplatCloud } from '../splat/format';

const { Dropdown } = ui;
type Axis = 'x' | 'y' | 'z';
const AXES: Axis[] = ['x', 'y', 'z'];

export interface SectionCardProps {
  scan: Scan;
  cloud: SplatCloud | null;
}

function HullPlot({ hull }: { hull: [number, number][] }) {
  if (hull.length < 3) return <div className="small text-body-secondary">No closed section at this position.</div>;
  const xs = hull.map((p) => p[0]), ys = hull.map((p) => p[1]);
  const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
  const span = Math.max(maxX - minX, maxY - minY, 1);
  const pad = span * 0.08;
  const vb = `${minX - pad} ${-(maxY + pad)} ${maxX - minX + 2 * pad} ${maxY - minY + 2 * pad}`;
  const pts = hull.map((p) => `${p[0]},${-p[1]}`).join(' ');
  return (
    <svg className="section-plot" viewBox={vb} preserveAspectRatio="xMidYMid meet" role="img" aria-label="Cross-section outline">
      <polygon points={pts} fill="rgba(20,184,166,0.25)" stroke="#14b8a6" strokeWidth={span / 150} />
    </svg>
  );
}

export function SectionCard({ scan, cloud }: SectionCardProps) {
  const [axis, setAxis] = useState<Axis>('z');
  const [frac, setFrac] = useState(0.5);
  const [result, setResult] = useState<SectionResult | null>(null);
  const [status, setStatus] = useState('');

  const bounds = useMemo(() => (cloud ? cloudBounds(cloud, [LABEL_OBJECT]) ?? cloudBounds(cloud, []) : null), [cloud]);
  const ai = AXES.indexOf(axis);
  const lo = bounds ? bounds.min[ai] : 0, hi = bounds ? bounds.max[ai] : 0;
  const atMm = (lo + (hi - lo) * frac) * 1000;
  const enabled = !!bounds && scan.has_splats;

  useEffect(() => {
    if (!enabled) return;
    const t = window.setTimeout(() => {
      setStatus('Measuring…');
      api
        .measureSection(scan.id, axis, atMm, 2)
        .then((r) => {
          setResult(r);
          setStatus('');
        })
        .catch((err) => {
          setResult(null);
          setStatus(errorMessage(err));
        });
    }, 300);
    return () => window.clearTimeout(t);
  }, [enabled, scan.id, axis, atMm]);

  return (
    <Card>
      <Card.Header>Cross-Section</Card.Header>
      <Card.Body>
        {!enabled && <span className="small text-body-secondary">Available once the scan has refined splats.</span>}
        {enabled && result && (
          <>
            <HullPlot hull={result.hull} />
            <div className="d-flex flex-wrap gap-3 small mt-2">
              <span>width <span className="measure-value">{fmtMm(result.width_mm)}</span></span>
              <span>depth <span className="measure-value">{fmtMm(result.depth_mm)}</span></span>
              <span>area <span className="measure-value">{result.area_mm2.toFixed(0)} mm²</span></span>
              <span>perimeter <span className="measure-value">{fmtMm(result.perimeter_mm)}</span></span>
            </div>
          </>
        )}
        {status && <div className="small text-body-secondary mt-1">{status}</div>}
      </Card.Body>
      <Card.Footer className="d-flex flex-wrap align-items-center gap-2">
        <Dropdown
          value={axis}
          options={AXES.map((a) => ({ value: a, label: `${a.toUpperCase()} axis` }))}
          onChange={(e) => setAxis(e.target.value as Axis)}
          disabled={!enabled}
          aria-label="Section axis"
        />
        <Form.Range
          className="flex-grow-1"
          style={{ minWidth: '8rem' }}
          min={0}
          max={1}
          step={0.005}
          value={frac}
          disabled={!enabled}
          onChange={(e) => setFrac(Number(e.target.value))}
          aria-label="Section position"
        />
        <span className="small measure-value">{axis} = {fmtMm(atMm)}</span>
      </Card.Footer>
    </Card>
  );
}
